/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import deleteRegisteredUser from "@stoneage/App/Handlers/CourseManagement/DeleteRegisteredUser";
import updateRegisteredUser from "@stoneage/App/Handlers/CourseManagement/UpdateRegisteredUser";
import { useThemeContext } from "@stoneage/App/Widgets/CourseManagement/CourseManagementCourseList";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import React, { useContext, useState } from "react";
import { HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

export interface CourseManagementEditStudentButtonStyles {
    editButtonsWrapper?: InjectableCss;
    editStudentButton?: LinkPresentationProps;
    reclaimSeatButton?: LinkPresentationProps;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
}

export const courseManagementEditStudentButtonStyles: CourseManagementEditStudentButtonStyles = {
    editStudentButton: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "Edit2",
                size: 12,
                color: "common.ui6",
                css: css`
                    margin-right: 4px;
                `,
            },
        },
    },
    reclaimSeatButton: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "IconClear",
                size: 8,
                color: "common.ui6",
                css: css`
                    margin-right: 4px;
                `,
            },
        },
    },
    editButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 23px 0;
            margin-bottom: 23px;
            border-bottom: 1px solid ${getColor("common.ui6")};
        `,
    },
    cancelButton: {
        variant: "secondary",
        showIcon: false,
        sizeVariant: "small",
    },
    saveButton: {
        showIcon: false,
        sizeVariant: "small",
    },
};

const styles = courseManagementEditStudentButtonStyles;

export interface CourseManagementEditStudentButtonProps {
    registrationId: string;
    courseId: string;
    firstName: string;
    lastName: string;
    company?: string;
    updateRegisteredUser: HandleThunkActionCreator<typeof updateRegisteredUser>;
    deleteRegisteredUser: HandleThunkActionCreator<typeof deleteRegisteredUser>;
}

const CourseManagementEditStudentButton = ({
    courseId,
    registrationId,
    firstName,
    lastName,
    company,
    updateRegisteredUser,
    deleteRegisteredUser,
}: CourseManagementEditStudentButtonProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const getRequiredErrorMessage = (displayName: string) => siteMessage("Field_Required", displayName);
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [firstNameChanged, setFirstName] = useState(firstName);
    const [lastNameChanged, setLastName] = useState(lastName);
    const [companyChanged, setCompany] = useState(company);
    const setUpdateCourses = useThemeContext();
    const validateFirstName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("First Name") : "";
        setFirstNameError(errorMessage as any);
        return !errorMessage;
    };

    const validateLastName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("Last Name") : "";
        setLastNameError(errorMessage as any);
        return !errorMessage;
    };

    const firstNameChangeHandler = (firstName: string) => {
        validateFirstName(firstName);
        setFirstName(firstName);
    };

    const lastNameChangeHandler = (lastName: string) => {
        validateLastName(lastName);
        setLastName(lastName);
    };

    const handleCancelClick = () => {
        setIsModalOpen(false);
        setLastName(lastName);
        setFirstName(firstName);
        setCompany(company);
    };

    const handleReclaimClick = () => {
        deleteRegisteredUser({
            courseId,
            registrationId,
            onError: error => {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
            onSuccess(resultProps) {
                if (resultProps?.successful) {
                    toasterContext.addToast({
                        body: translate("Student has been successfully Reclaimed"),
                        messageType: "success",
                    });
                    setUpdateCourses((prevValue: boolean) => {
                        return !prevValue;
                    });
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                handleCancelClick();
            },
        });
    };

    const handleSaveEditsClick = () => {
        updateRegisteredUser({
            firstName: firstNameChanged,
            lastName: lastNameChanged,
            company: companyChanged || "",
            courseId,
            registrationId,
            onError: error => {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
            onSuccess(resultProps) {
                if (resultProps?.successful) {
                    toasterContext.addToast({
                        body: translate("Student has been successfully Edited"),
                        messageType: "success",
                    });
                    setUpdateCourses((prevValue: boolean) => {
                        return !prevValue;
                    });
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                handleCancelClick();
            },
        });
    };

    const disabledSaceButton =
        company === companyChanged && lastName === lastNameChanged && firstName === firstNameChanged;

    return (
        <>
            <Link
                {...styles.editStudentButton}
                data-test-selector="courseManagemet_editStudentButton"
                onClick={() => setIsModalOpen(true)}
            >
                {translate("Edit Student")}
            </Link>
            <Modal
                size={350}
                headline={translate("Edit Student")}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            >
                <StyledWrapper>
                    <StyledWrapper>
                        <TextField
                            label={translate("First Name")}
                            placeholder={translate("Student's First Name")}
                            value={firstNameChanged}
                            onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                            error={firstNameError}
                            required
                        />
                    </StyledWrapper>
                    <StyledWrapper
                        css={css`
                            margin: 16px 0;
                        `}
                    >
                        <TextField
                            label={translate("Last Name")}
                            placeholder={translate("Student's Last Name")}
                            value={lastNameChanged}
                            onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                            error={lastNameError}
                            required
                        />
                    </StyledWrapper>
                    <StyledWrapper>
                        <TextField
                            label={translate("Company")}
                            placeholder={translate("Company Name (Optional)")}
                            value={companyChanged}
                            onChange={e => setCompany(e.currentTarget.value)}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.editButtonsWrapper}>
                        <Button {...styles.cancelButton} onClick={handleCancelClick}>
                            {translate("Cancel")}
                        </Button>
                        <Button {...styles.saveButton} disabled={disabledSaceButton} onClick={handleSaveEditsClick}>
                            {translate("Save Edits")}
                        </Button>
                    </StyledWrapper>
                    <Link {...styles.reclaimSeatButton} onClick={handleReclaimClick}>
                        {translate("Reclaim Seat")}
                    </Link>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default CourseManagementEditStudentButton;
